import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PriceHistoryWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import { formatNumber, formatKMBT } from '../../utils/utils';

import {
  GetSectorIndicesResponseType,
  SectorIndicesDataType,
  SectorData,
} from '../../types/indices.type';
import { getSectorIndices } from '../../api/quote.api';
import { ReactComponent as SortIcon } from '../../assets/icons/arrow-down-s-fill.svg';
import {
  Region,
  ThemeType,
  TimePeriod,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import {
  GetPriceHistoryResponseType,
  PriceHistoryDataType,
} from '../../types/chart5.type';
import { getPriceHistory } from '../../api/chart5.api';
import Pagination from '../../Components/Common/Pagination/Pagination';
import {
  paginate,
  PaginationType,
  getMaxPageNum,
} from '../../utils/pagination';
import { THEME_COLOR_MAPPING } from '../../utils/constant';
import Layout from '../../Components/Common/Layout/Layout';
import moment from 'moment';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';

export interface PriceHistoryWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  frDate?: string;
  toDate?: string | undefined;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const PriceHistoryWidget = (props: PriceHistoryWidgetProps) => {
  const { onHeightChange } = props;

  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'priceHistory');
  const [prevPage, setPrevPage] = useState(1);
  const sortcolMap = {
    '1D': 'perf1D',
    '1M': 'perf1M',
    '3M': 'perf3M',
    '6M': 'perf6M',
    '1YR': 'perf1Y',
  };
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.PriceHistory, onHeightChange);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { width, isSmallScreen } = useContainerWidth(widgetRef);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [data, setData] = useState<PriceHistoryDataType[]>([]);
  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);
  const RESULTS_PER_PAGE = 10;
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationType>(
    paginate(page, RESULTS_PER_PAGE, total, maxPageNum),
  );

  const [fr, setFr] = useState(
    props.frDate ?? moment().subtract(6, 'months').format('YYYYMMDD'),
  ); // default is 6 months ago
  const [to, setTo] = useState<string | undefined>(props.toDate ?? undefined); // default is today

  // Update pagination when total changes
  useEffect(() => {
    setPagination(paginate(page, RESULTS_PER_PAGE, total, maxPageNum));
  }, [total, page, maxPageNum]);

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, false, false, false));
    }
  }, [width]);

  // scroll to widgetRef current position when page changes. Don't scroll if page is the same.
  useEffect(() => {
    if (tableContainerRef.current) {
      if (page !== prevPage) {
        tableContainerRef.current.scrollTop = 0;
        setPrevPage(page);
      }
    }
  }, [page, prevPage]);

  // Initial state for reset functionality
  const initialColor = [
    {
      color: '#085026',
      fontColor: '#ffffff',
      min: 5,
      max: 9999.0,
      label: '+5%',
    },
    { color: '#11773c', fontColor: '#ffffff', min: 4, max: 5, label: '+4%' },
    { color: '#188647', fontColor: '#ffffff', min: 3, max: 4, label: '+3%' },
    { color: '#1C7D45', fontColor: '#ffffff', min: 2, max: 3, label: '+2%' },
    { color: '#34b66b', fontColor: '#ffffff', min: 1, max: 2, label: '+1%' },
    {
      color: '#99ddb6',
      fontColor: '#ffffff',
      min: 0.5,
      max: 1,
      label: '+0.5%',
    },
    {
      color: '#7f7f7f',
      fontColor: '#ffffff',
      min: -0.5,
      max: 0.5,
      label: '0%',
    },
    {
      color: '#ffbcbc',
      fontColor: '#ffffff',
      min: -1,
      max: -0.5,
      label: '-0.5%',
    },
    {
      color: '#f18989',
      fontColor: '#ffffff',
      min: -2.0,
      max: -1.0,
      label: '-1%',
    },
    {
      color: '#e73838',
      fontColor: '#ffffff',
      min: -3.0,
      max: -2.0,
      label: '-2%',
    },
    {
      color: '#d81f1f',
      fontColor: '#ffffff',
      min: -4.0,
      max: -3.0,
      label: '-3%',
    },
    {
      color: '#b21b1b',
      fontColor: '#ffffff',
      min: -5.0,
      max: -4.0,
      label: '-4%',
    },
    {
      color: '#9e1111',
      fontColor: '#ffffff',
      min: -9999.0,
      max: -5.0,
      label: '-5%',
    },
  ];

  // Call API when props changes

  useEffect(() => {
    if (props.frDate) {
      setFr(props.frDate);
    }
    if (props.toDate) {
      setTo(props.toDate);
    }
  }, [props.frDate, props.toDate]);

  useEffect(() => {
    fetchPriceHistoryData();
  }, [props.token, props.symbol, fr, to]);

  const fetchPriceHistoryData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    //console.log('fetchPriceHistoryData', props.ric);
    try {
      const res: GetPriceHistoryResponseType = await getPriceHistory(
        props.symbol || '',
        props.token || '',
        moment(fr, 'YYYY-MM-DD').format('YYYYMMDD'),
        to ? moment(to, 'YYYY-MM-DD').format('YYYYMMDD') : undefined,
      );
      if (res) {
        setData(res);
        setIsEmptyData(res.length === 0);
        if (res.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'PriceHistoryWidget',
            data: null,
          });
        }
        let total = res.filter(
          (item) => item.timestamp !== moment().format('DD/MM/YYYY'),
        ).length;
        setTotal(total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch price history data');
      console.error('Error fetching price history data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol, fr, to]);

  // Get Broker Rating Class
  const getRecommendationClass = (recommendation: string) => {
    switch (recommendation) {
      case 'Strong Buy':
        return 'rec-strong-buy';
      case 'Buy':
        return 'rec-buy';
      case 'Hold':
        return 'rec-hold';
      case 'Reduce':
        return 'rec-reduce';
      case 'Sell':
        return 'rec-sell';
      default:
        return 'rec-hold';
    }
  };

  function getCompanyScoreClass(value: number): string {
    let scoreClass = '';
    if (value) {
      switch (value) {
        case 10:
          scoreClass = 'score-10';
          break;
        default:
          scoreClass = 'score';
      }
    }

    return scoreClass;
  }

  function getUpDownClass(value: number): string {
    let updownclass = '';
    if (value) {
      if (value > 0) updownclass = 'positive';
      else if (value < 0) updownclass = 'negative';
      else if ((value = 0)) updownclass = 'nochange';
    }

    return updownclass;
  }

  function getHeatmapStyle(value: number): any {
    for (const range of initialColor) {
      if (value >= range.min && value < range.max) {
        return { backgroundColor: range.color, color: range.fontColor };
      }
    }
    return null;
  }

  function cutOffWords(text: string): string {
    const maxChars = isSmallScreen ? 50 : 22;
    const isLong = text.length > maxChars;
    const shown = !isLong ? text : text.slice(0, maxChars) + '…';
    return shown;
  }

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="ph-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="ph-content-wrapper">
            <div ref={tableContainerRef} className="ph-table-container">
              <table className="ph-table">
                <thead>
                  {
                    <tr>
                      <th className=""></th>
                      <th className="date">Today</th>
                      {!isSmallScreen && <th>Open</th>}
                      {!isSmallScreen && <th>High</th>}
                      {!isSmallScreen && <th>Low</th>}
                      <th>Last price</th>
                      <th>%Chg</th>
                      {!isSmallScreen && <th>Volume</th>}
                      <th></th>
                    </tr>
                  }
                </thead>
                <tbody>
                  {data ? (
                    data.slice(0, 1).map((item, index) => {
                      let today = moment().format('DD/MM/YYYY');
                      let isToday = false;
                      if (item.timestamp === today) {
                        isToday = true;
                      }
                      return (
                        <tr key={index}>
                          <td className=""></td>
                          <td className="date">{today}</td>
                          {!isSmallScreen && (
                            <td>{isToday ? item.open : '-'}</td>
                          )}
                          {!isSmallScreen && (
                            <td>{isToday ? item.high : '-'}</td>
                          )}
                          {!isSmallScreen && (
                            <td>{isToday ? item.low : '-'}</td>
                          )}
                          <td>{isToday ? item.close : '-'}</td>
                          <td
                            className={getUpDownClass(
                              Number(item.percentageChange),
                            )}
                          >
                            {isToday ? item.percentageChange : '-'}
                          </td>
                          {!isSmallScreen && (
                            <td>
                              {isToday
                                ? formatNumber(
                                    item.volume,
                                    0,
                                    0,
                                    false,
                                    false,
                                    false,
                                  )
                                : '-'}
                            </td>
                          )}
                          <td className="lastcolumn"></td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} className="no-data">
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
                <thead>
                  {
                    <tr>
                      <th className=""></th>
                      <th className="date">Date</th>
                      {!isSmallScreen && <th>Open</th>}
                      {!isSmallScreen && <th>High</th>}
                      {!isSmallScreen && <th>Low</th>}
                      <th>Close</th>
                      <th>%Chg</th>
                      {!isSmallScreen && <th>Volume</th>}
                      <th></th>
                    </tr>
                  }
                </thead>
                <tbody>
                  {data ? (
                    data
                      .filter(
                        (item) =>
                          item.timestamp !== moment().format('DD/MM/YYYY'),
                      )
                      .slice(
                        (page - 1) * RESULTS_PER_PAGE,
                        page * RESULTS_PER_PAGE,
                      )
                      .map((item, index) => {
                        return (
                          <tr key={index}>
                            <td className=""></td>
                            <td className="date">{item.timestamp}</td>
                            {!isSmallScreen && <td>{item.open}</td>}
                            {!isSmallScreen && <td>{item.high}</td>}
                            {!isSmallScreen && <td>{item.low}</td>}
                            <td>{item.close}</td>
                            <td
                              className={getUpDownClass(
                                Number(item.percentageChange),
                              )}
                            >
                              {item.percentageChange}
                            </td>
                            {!isSmallScreen && (
                              <td>
                                {formatNumber(
                                  item.volume,
                                  0,
                                  0,
                                  false,
                                  false,
                                  false,
                                )}
                              </td>
                            )}
                            <td className="lastcolumn"></td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan={10} className="no-data">
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="ph-pagination-container">
              {
                <Pagination
                  pagination={pagination}
                  onPageClick={(targetPage: number) => {
                    setPage(targetPage);
                  }}
                  haveLastPage={true}
                />
              }
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PriceHistoryWidget;
